import { createContext, useEffect, useState } from "react"

import { type LogoutOptions, useAuth0 } from "@auth0/auth0-react"
import { useRouter } from "expo-router"

import { BackendContextValue, setup } from "wealthmode-frontend/backend"

import { Config } from "@/Config"
import { validateAuthOnPageLoad } from "@/lib/validateStoredAuth"

export const BackendContext = createContext<BackendContextValue>({} as BackendContextValue)

async function clearSessionAndRedirectToLogin(
  logout: (options?: LogoutOptions) => Promise<void>,
  router: ReturnType<typeof useRouter>,
) {
  // `openUrl: false` is v2's replacement for v1's `localOnly`: drop the local
  // session without bouncing through Auth0, so the replace below is what runs.
  await logout({ openUrl: false })
  router.replace("/login")
}

export function BackendProvider({ children }: { children: any }) {
  const { getIdTokenClaims, logout } = useAuth0()
  const router = useRouter()

  const [loading, setLoading] = useState<boolean>(true)
  const [value, setValue] = useState<BackendContextValue>({} as BackendContextValue)

  useEffect(() => {
    async function start() {
      const cfg = Config.get("api") as {
        base_url: string
      }
      const claims = await getIdTokenClaims()
      const id_token = claims?.__raw as string | undefined

      if (!id_token) {
        await clearSessionAndRedirectToLogin(logout, router)
        return
      }

      const valid = await validateAuthOnPageLoad(cfg.base_url, id_token)
      if (!valid) {
        await clearSessionAndRedirectToLogin(logout, router)
        return
      }

      const roolURL = new URL(cfg.base_url)

      setup(roolURL, id_token, () => {
        router.replace("/logout")
      })

      const newVal = new BackendContextValue()
      await newVal
        .init(roolURL)
        .then(() => {
          setValue(newVal)
          setLoading(false)
        })
        .catch((err) => {
          console.log("Error initializing backend connection: " + err)
        })
    }

    start()
  }, [])

  return <>{loading ? <></> : <BackendContext.Provider value={value}>{children}</BackendContext.Provider>}</>
}
